import {
  type ExplicitSelectionCard,
  type ExplicitSelectionTag,
  type MatchingTagChip,
} from '@hungryroot/pho';
import { tagGroups, tags } from '@hungryroot/tags';

// A swipe card shows at most this many badges.
const MAX_BADGES = 2;

// The Eggs breakfast tag card (id 28) is the only tag-backed category card that
// carries badges today; it has no underlying recipe tags, so they're hardcoded.
const EGG_TAG_ID = 28;

/**
 * Catalog tag IDs attached to pairings/products for swipe-deck badges
 * (product/data confirmed). Four of the five match the food-format quiz page's
 * tag_ids 1:1 (ready-to-eat 810, quick recipes 817, chef-style 809, family
 * 352); the lone exception is heat-and-eat, which pairings carry as 297 while
 * the quiz page selects it as tags.HeatAndEat (379). Food-format badges are
 * matched off the card's *catalog* tags via the map below, and the customer's
 * quiz selections are translated into catalog space (see
 * QUIZ_TO_CATALOG_FOOD_FORMAT_TAG_IDS) before Layer-1 matching so a heat-and-eat
 * selection still matches a heat-and-eat recipe.
 */
export const SWIPE_DECK_FOOD_FORMAT_TAG_IDS = {
  quickRecipes: tags.QuickRecipes,
  chefStyleRecipes: tags.ChefStyleRecipes,
  familyMeals: tags.FamilyMeals,
  readyToEat: tags.ReadyToEat,
  heatAndEat: 297,
} as const;

/** Exp 220-only catalog food-format tag ids (not on the default badge map). */
export const EXP_220_FOOD_FORMAT_TAG_IDS = {
  easyPrepSalads: tags.EasyPrepSalads,
  ovenReadyEntrees: tags.OvenReadyEntrees,
} as const;

/**
 * Exp 220 food-format badge priority. Derived from the backend dynamic config
 * `explicit_selections.exp_220.food_format_priority` default
 * (he, rte, chef, family, oven_ready_entrees, easy_prep_salads, quick) with
 * one adjustment: oven-ready and easy-prep outrank chef, family, and quick
 * so a dual-tagged card in a new-format slot never shows a legacy badge.
 * Pairs among he/rte/new formats still follow the config order.
 */
export const EXP_220_FOOD_FORMAT_TAG_ORDER = [
  SWIPE_DECK_FOOD_FORMAT_TAG_IDS.heatAndEat,
  SWIPE_DECK_FOOD_FORMAT_TAG_IDS.readyToEat,
  EXP_220_FOOD_FORMAT_TAG_IDS.ovenReadyEntrees,
  EXP_220_FOOD_FORMAT_TAG_IDS.easyPrepSalads,
  SWIPE_DECK_FOOD_FORMAT_TAG_IDS.chefStyleRecipes,
  SWIPE_DECK_FOOD_FORMAT_TAG_IDS.familyMeals,
  SWIPE_DECK_FOOD_FORMAT_TAG_IDS.quickRecipes,
] as const;

/**
 * Translates the food-format tag_ids the quiz page selects into the catalog
 * tag_ids pairings/products carry, for Layer-1 badge matching. Only heat-and-eat
 * diverges (quiz 379 → catalog 297); every other tag maps to itself, so callers
 * can run their full `liked_tags` set through `toCatalogFoodFormatTagIds`.
 */
export const QUIZ_TO_CATALOG_FOOD_FORMAT_TAG_IDS: Readonly<
  Record<number, number>
> = {
  [tags.HeatAndEat]: SWIPE_DECK_FOOD_FORMAT_TAG_IDS.heatAndEat,
};

/**
 * Maps quiz-space tag ids into catalog space, leaving any id without a mapping
 * untouched. Safe to run over the entire `liked_tags` set since it's identity
 * for everything but the diverging food-format tags.
 */
export function toCatalogFoodFormatTagIds(ids: readonly number[]): number[] {
  return ids.map((id) => QUIZ_TO_CATALOG_FOOD_FORMAT_TAG_IDS[id] ?? id);
}

/** Product-doc badge strings for in-quiz food format tags. */
const FOOD_FORMAT_BADGE_LABEL: Readonly<Record<number, string>> = {
  [SWIPE_DECK_FOOD_FORMAT_TAG_IDS.quickRecipes]: 'Quick Recipe',
  [SWIPE_DECK_FOOD_FORMAT_TAG_IDS.chefStyleRecipes]: 'Chef-Style Recipe',
  [SWIPE_DECK_FOOD_FORMAT_TAG_IDS.familyMeals]: 'Family Recipe',
  [SWIPE_DECK_FOOD_FORMAT_TAG_IDS.readyToEat]: 'Ready-to-eat',
  [SWIPE_DECK_FOOD_FORMAT_TAG_IDS.heatAndEat]: 'Heat & Eat',
};

const EXP_220_FOOD_FORMAT_BADGE_LABEL: Readonly<Record<number, string>> = {
  ...FOOD_FORMAT_BADGE_LABEL,
  [EXP_220_FOOD_FORMAT_TAG_IDS.easyPrepSalads]: 'Easy prep salad',
  [EXP_220_FOOD_FORMAT_TAG_IDS.ovenReadyEntrees]: 'Oven-ready entree',
};

// Hardcoded badges for tag-backed category cards, which have no recipe tags to
// derive badges from. Only the Eggs card is badged today.
const TAG_CARD_BADGES: Readonly<Record<number, MatchingTagChip[]>> = {
  [EGG_TAG_ID]: [
    { id: 'breakfast', label: 'Breakfast' },
    { id: 'high-protein', label: 'High protein' },
  ],
};

type BadgeCategory =
  | 'foodFormat'
  | 'nutrition'
  | 'quality'
  | 'cuisine'
  | 'dishType';

// tagGroups.certificates (10) is the "Qualities" group despite the constant
// name. Dish types span the lunch/dinner/breakfast dish-type groups.
const DISH_TYPE_GROUP_IDS = new Set<number>([
  tagGroups.LunchDishTypes,
  tagGroups.DinnerDishTypes,
  tagGroups.BreakfastDishTypes,
]);

// Layer 1 (tags the customer selected in the quiz that also tag the recipe) can
// badge any of the five categories; layer 2 (the recipe's own tags, regardless
// of selection) only fills food format, cuisine, and dish type.
const LAYER_1_CATEGORY_ORDER: readonly BadgeCategory[] = [
  'foodFormat',
  'nutrition',
  'quality',
  'cuisine',
  'dishType',
];
const LAYER_2_CATEGORY_ORDER: readonly BadgeCategory[] = [
  'foodFormat',
  'cuisine',
  'dishType',
];

interface CategorizedTag {
  tag: ExplicitSelectionTag;
  category: BadgeCategory;
  label: string;
}

// Classify a card tag into one badge category (or none). Food format is keyed
// off the caller's label map; the rest are keyed off the tag group.
function categorizeTag(
  tag: ExplicitSelectionTag,
  foodFormatLabelMap: Readonly<Record<number, string>>
): CategorizedTag | null {
  const foodFormatLabel = foodFormatLabelMap[tag.id];
  if (foodFormatLabel !== undefined) {
    return { tag, category: 'foodFormat', label: foodFormatLabel };
  }
  if (tag.tagGroupId === tagGroups.nutrition) {
    return { tag, category: 'nutrition', label: tag.name };
  }
  if (tag.tagGroupId === tagGroups.certificates) {
    return { tag, category: 'quality', label: tag.name };
  }
  if (tag.tagGroupId === tagGroups.cuisine) {
    return { tag, category: 'cuisine', label: tag.name };
  }
  if (tag.tagGroupId !== null && DISH_TYPE_GROUP_IDS.has(tag.tagGroupId)) {
    return { tag, category: 'dishType', label: tag.name };
  }
  return null;
}

/**
 * Swipe-deck badges for the funnel deck.
 *
 * Fills up to `MAX_BADGES` slots, one badge per category, in two layers:
 *
 * 1. Tags the customer *selected* in the quiz (`likedTagIds`, from
 *    `buildQuizInputParams(quiz).liked_tags`) that also tag the recipe, ordered
 *    food format → nutrition → quality → cuisine → dish type.
 * 2. If slots remain, the recipe's own tags regardless of selection, ordered
 *    food format → cuisine → dish type.
 *
 * Food format is always resolved from the card's catalog tags via the hardcoded
 * label map, never the customer's (differently-id'd) quiz food-format answer.
 *
 * Exp 220 treatment extends the label map and food-format priority order for
 * oven-ready entrees and easy prep salads; all other decks use the default map.
 */
export function resolveSwipeDeckBadge({
  card,
  likedTagIds,
  isExp220Treatment = false,
}: {
  card: Pick<ExplicitSelectionCard, 'itemType' | 'itemId' | 'tags'>;
  likedTagIds: ReadonlySet<number>;
  isExp220Treatment?: boolean;
}): MatchingTagChip[] {
  if (card.itemType === 'tag') {
    return TAG_CARD_BADGES[card.itemId] ?? [];
  }

  const foodFormatLabelMap = isExp220Treatment
    ? EXP_220_FOOD_FORMAT_BADGE_LABEL
    : FOOD_FORMAT_BADGE_LABEL;

  const categorized = (card.tags ?? [])
    .map((tag) => categorizeTag(tag, foodFormatLabelMap))
    .filter((entry): entry is CategorizedTag => entry !== null);

  const chips: MatchingTagChip[] = [];
  const filledCategories = new Set<BadgeCategory>();
  const seenLabels = new Set<string>();

  function pickFoodFormatMatch(
    pool: readonly CategorizedTag[]
  ): CategorizedTag | undefined {
    const foodFormats = pool.filter((entry) => entry.category === 'foodFormat');
    if (foodFormats.length === 0) return undefined;
    if (!isExp220Treatment) {
      return foodFormats[0];
    }
    for (const tagId of EXP_220_FOOD_FORMAT_TAG_ORDER) {
      const match = foodFormats.find((entry) => entry.tag.id === tagId);
      if (match !== undefined) return match;
    }
    return undefined;
  }

  function fillFrom(
    pool: readonly CategorizedTag[],
    categoryOrder: readonly BadgeCategory[]
  ): void {
    for (const category of categoryOrder) {
      if (chips.length === MAX_BADGES) return;
      if (filledCategories.has(category)) continue;

      const match =
        category === 'foodFormat'
          ? pickFoodFormatMatch(pool)
          : pool.find((entry) => entry.category === category);
      if (match === undefined) continue;

      // One badge per category; dedupe by label as a safety net.
      filledCategories.add(category);
      const labelKey = match.label.trim().toLowerCase();
      if (seenLabels.has(labelKey)) continue;
      seenLabels.add(labelKey);
      chips.push({ id: String(match.tag.id), label: match.label });
    }
  }

  // Layer 1: the customer's matching quiz selections.
  fillFrom(
    categorized.filter((entry) => likedTagIds.has(entry.tag.id)),
    LAYER_1_CATEGORY_ORDER
  );
  // Layer 2: fill any remaining slots from the recipe's own tags.
  fillFrom(categorized, LAYER_2_CATEGORY_ORDER);

  return chips;
}
